// Phase 2: Core Function Migration
import { AggregateFunction, } from "./function-registry";
import { MISSING_VALUE } from "./type-registry";
import { SCALAR_UNIT } from "../unit-system";
// Helper to drop empty elements and check that the survivors share a unit.
// Empty cells surface as NaN (evaluator's table-column path) — they are
// skipped, not treated as errors, so a column with blanks doesn't poison the
// aggregate. Units are still checked strictly across the present values.
function presentNumericElements(values) {
    return values.filter((el) => typeof el.value === "number" && !Number.isNaN(el.value));
}
function checkArrayUnits(values) {
    const present = presentNumericElements(values);
    if (present.length === 0) {
        return { unit: SCALAR_UNIT, numbers: [] };
    }
    const unit = present[0].unit ?? SCALAR_UNIT;
    const numbers = [];
    for (let i = 0; i < present.length; i++) {
        const element = present[i];
        if ((element.unit ?? SCALAR_UNIT) !== unit) {
            return {
                type: "UnitMismatch",
                message: `All values must have the same unit. Expected "${unit}" but got "${element.unit}" at index ${i}`,
            };
        }
        numbers.push(element.value);
    }
    return { unit, numbers };
}
// Average function implementation
export class AvgFunction extends AggregateFunction {
    signature = {
        name: "avg",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array of numeric values to average",
            },
        ],
        returnType: "scalar",
        description: "Calculate the arithmetic mean of an array of numbers",
        examples: ["avg([1, 2, 3, 4, 5])", "avg(Measurements.Temperature)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        const unitCheck = checkArrayUnits(values);
        if ("type" in unitCheck)
            return unitCheck;
        const { unit, numbers } = unitCheck;
        // No values present (all inputs empty) → blank cell, no error.
        if (numbers.length === 0)
            return MISSING_VALUE;
        const sum = numbers.reduce((acc, val) => acc + val, 0);
        const average = sum / numbers.length;
        return {
            type: "scalar",
            value: average,
            unit,
        };
    }
}
// Sum function implementation
export class SumFunction extends AggregateFunction {
    signature = {
        name: "sum",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array of numeric values to sum",
            },
        ],
        returnType: "scalar",
        description: "Calculate the sum of an array of numbers",
        examples: ["sum([1, 2, 3, 4, 5])", "sum(Measurements.Temperature)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        const unitCheck = checkArrayUnits(values);
        if ("type" in unitCheck)
            return unitCheck;
        const { unit, numbers } = unitCheck;
        // No values present (all inputs empty) → blank cell, no error.
        if (numbers.length === 0)
            return MISSING_VALUE;
        const sum = numbers.reduce((acc, val) => acc + val, 0);
        return {
            type: "scalar",
            value: sum,
            unit,
        };
    }
}
// Min function implementation
export class MinFunction extends AggregateFunction {
    signature = {
        name: "min",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array of numeric values to find minimum",
            },
        ],
        returnType: "scalar",
        description: "Find the minimum value in an array of numbers",
        examples: ["min([1, 2, 3, 4, 5])", "min(Measurements.Temperature)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        const unitCheck = checkArrayUnits(values);
        if ("type" in unitCheck)
            return unitCheck;
        const { unit, numbers } = unitCheck;
        // No values present (all inputs empty) → blank cell, no error.
        if (numbers.length === 0)
            return MISSING_VALUE;
        const minimum = Math.min(...numbers);
        return {
            type: "scalar",
            value: minimum,
            unit,
        };
    }
}
// Max function implementation
export class MaxFunction extends AggregateFunction {
    signature = {
        name: "max",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array of numeric values to find maximum",
            },
        ],
        returnType: "scalar",
        description: "Find the maximum value in an array of numbers",
        examples: ["max([1, 2, 3, 4, 5])", "max(Measurements.Temperature)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        const unitCheck = checkArrayUnits(values);
        if ("type" in unitCheck)
            return unitCheck;
        const { unit, numbers } = unitCheck;
        // No values present (all inputs empty) → blank cell, no error.
        if (numbers.length === 0)
            return MISSING_VALUE;
        const maximum = Math.max(...numbers);
        return {
            type: "scalar",
            value: maximum,
            unit,
        };
    }
}
// Standard deviation function (sample std, N-1)
export class StdFunction extends AggregateFunction {
    signature = {
        name: "std",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array of numeric values",
            },
        ],
        returnType: "scalar",
        description: "Sample standard deviation (N-1) of an array of numbers",
        examples: ["std(BTH.dm_oven.dm)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        const unitCheck = checkArrayUnits(values);
        if ("type" in unitCheck)
            return unitCheck;
        const { unit, numbers } = unitCheck;
        // Sample std needs ≥2 present values; otherwise the cell is blank, not an
        // error (a partially-filled row is incomplete, not wrong).
        if (numbers.length < 2)
            return MISSING_VALUE;
        const mean = numbers.reduce((a, b) => a + b, 0) / numbers.length;
        const variance = numbers.reduce((a, b) => a + (b - mean) ** 2, 0) / (numbers.length - 1);
        return {
            type: "scalar",
            value: Math.sqrt(variance),
            unit,
        };
    }
}
// Single — returns the only element of an array, errors if length !== 1
export class SingleFunction extends AggregateFunction {
    signature = {
        name: "single",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array expected to contain exactly one element",
            },
        ],
        returnType: "scalar",
        description: "Extract the single value from an array. Errors if the array has zero or more than one element.",
        examples: ["single(FINAL.dm_oven.dm)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        if (values.length === 0) {
            return context.createError("ExpectedSingleElement", "single() called on empty array");
        }
        if (values.length > 1) {
            return context.createError("ExpectedSingleElement", `single() expected exactly 1 element, got ${values.length}`);
        }
        const el = values[0];
        if (typeof el.value !== "number") {
            return context.createError("InvalidArgumentType", "single() element must be numeric");
        }
        return {
            type: "scalar",
            value: el.value,
            unit: el.unit ?? SCALAR_UNIT,
        };
    }
}
// ArrayFirst — first element of an array (overloads `first` which also exists for tables)
export class ArrayFirstFunction extends AggregateFunction {
    signature = {
        name: "first",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array to take the first element from",
            },
        ],
        returnType: "scalar",
        description: "First element of an array",
        examples: ["first(BTH.dm_oven.dm)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        const present = presentNumericElements(values);
        if (present.length === 0)
            return MISSING_VALUE;
        const el = present[0];
        return {
            type: "scalar",
            value: el.value,
            unit: el.unit ?? SCALAR_UNIT,
        };
    }
}
// ArrayLast — last element of an array
export class ArrayLastFunction extends AggregateFunction {
    signature = {
        name: "last",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array to take the last element from",
            },
        ],
        returnType: "scalar",
        description: "Last element of an array",
        examples: ["last(BTH.dm_oven.dm)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        const present = presentNumericElements(values);
        if (present.length === 0)
            return MISSING_VALUE;
        const el = present[present.length - 1];
        return {
            type: "scalar",
            value: el.value,
            unit: el.unit ?? SCALAR_UNIT,
        };
    }
}
// ArrayNth — nth element (0-indexed) of an array. Not an AggregateFunction
// because the index argument must NOT be flattened into the value list; indexes
// into the present (non-empty) elements so nth(a, 0) matches first(a).
export class ArrayNthFunction {
    signature = {
        name: "nth",
        parameters: [
            { name: "values", type: "array", description: "Array to index into" },
            { name: "index", type: "scalar", description: "Zero-based index" },
        ],
        returnType: "scalar",
        description: "Nth element (0-indexed) of an array",
        examples: ["nth(measurements.temperature, 2)"],
        category: "aggregate",
    };
    execute(args, context) {
        const arr = args[0];
        const idx = args[1];
        if (arr.type === "missing" || idx.type === "missing")
            return MISSING_VALUE;
        if (arr.type !== "array") {
            return context.createError("InvalidArgumentType", `nth() expects an array, but received type "${arr.type}".`);
        }
        if (idx.type !== "scalar" || typeof idx.value !== "number") {
            return context.createError("InvalidArgumentType", "nth() index must be a number.");
        }
        const present = presentNumericElements(arr.value);
        const i = Math.floor(idx.value);
        if (i < 0 || i >= present.length) {
            return context.createError("IndexOutOfBounds", `Array index ${i} is out of bounds. Array has ${present.length} elements.`);
        }
        const el = present[i];
        return { type: "scalar", value: el.value, unit: el.unit ?? SCALAR_UNIT };
    }
}
// Count function implementation
export class CountFunction extends AggregateFunction {
    signature = {
        name: "count",
        parameters: [
            {
                name: "values",
                type: ["scalar", "array"],
                variadic: true,
                description: "Array of values to count",
            },
        ],
        returnType: "scalar",
        description: "Count the number of elements in an array",
        examples: ["count([1, 2, 3, 4, 5])", "count(Measurements.Temperature)"],
        category: "aggregate",
    };
    aggregate(values, context) {
        // Counts present values only; empty inputs are not counted. An all-empty
        // row yields 0 (a meaningful count), not a blank.
        return {
            type: "scalar",
            value: presentNumericElements(values).length,
            unit: SCALAR_UNIT,
        };
    }
}
/**
 * `if(condition, then, else)`. Only the chosen branch is evaluated, so the
 * other may reference blank inputs or divide by zero without failing the
 * formula (e.g. a liquid sample's broth amount on a powder sample).
 */
export class IfFunction {
    signature = {
        name: "if",
        parameters: [
            {
                name: "condition",
                type: ["boolean", "scalar"],
                description: "A comparison, or a number where 0 is false",
            },
            { name: "then", type: "any", lazy: true, description: "Value when true" },
            { name: "else", type: "any", lazy: true, description: "Value when false" },
        ],
        returnType: "scalar",
        description: "Choose between two values by a condition",
        examples: ["if(is_liquid, volume / broth, 1)", "if(area > 0, area, 0)"],
        category: "logic",
    };
    execute(args, context) {
        const [condition, whenTrue, whenFalse] = args;
        const truthy = condition.type === "boolean"
            ? condition.value === true
            : condition.type === "scalar"
                ? condition.value !== 0
                : null;
        if (truthy === null) {
            return context.createError("InvalidArgumentType", `Function "if" expects a condition, got "${condition.type}".`);
        }
        const branch = truthy ? whenTrue : whenFalse;
        if (branch?.type !== "deferred") {
            return context.createError("InvalidArgumentType", 'Function "if" expects a value to return.');
        }
        return context.evaluateInRowScope(branch.value, new Map());
    }
}
/**
 * `notdetected()` — the analyte was looked for and not found. Propagates like
 * an error through every formula that uses it, and is stored as a
 * NOT_DETECTED result rather than an evaluation error.
 */
export class NotDetectedFunction {
    signature = {
        name: "notdetected",
        parameters: [],
        returnType: "scalar",
        description: "The analyte was not found: the result is reported as not detected",
        examples: ["if(area > 0, area, notdetected())"],
        category: "logic",
    };
    execute(_args, context) {
        return context.createError("NotDetected", "Not detected.");
    }
}
/**
 * `notapplicable()` — the value does not apply to this test, e.g. a liquid
 * concentration for a powder sample. Propagates like an error and is stored
 * as a NOT_APPLICABLE result rather than an evaluation error.
 */
export class NotApplicableFunction {
    signature = {
        name: "notapplicable",
        parameters: [],
        returnType: "scalar",
        description: "The value does not apply to this test: it is shown as n/a",
        examples: ["if(is_liquid, volume / broth, notapplicable())"],
        category: "logic",
    };
    execute(_args, context) {
        return context.createError("NotApplicable", "Not applicable.");
    }
}
//# sourceMappingURL=core-functions.js.map